import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { KeyRound, Plus } from "lucide-react";
import {
  API_KEY_EXPIRATION_DAYS,
  createApiKeyInputSchema,
  type ApiKey,
  type CreateApiKeyInput,
} from "@repo/shared";
import {
  Alert,
  Button,
  CopyField,
  DataTable,
  DialogClose,
  Field,
  Form,
  FormActions,
  FormDrawer,
  Input,
  localTablePage,
  Mono,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingsSection,
  StatusBadge,
  TableIdentity,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";

import { ConfirmDialog, toast, useConfirmDialog } from "@/components/shared";
import { apiKeysQueryOptions, useCreateApiKey, useRevokeApiKey } from "@/features/api-keys/api";
import { API_BASE_URL, getErrorMessage } from "@/lib/api-client";
import { discardMessages, listRefreshing, paginationLabels, tableMessages } from "@/lib/ui-labels";
import { useTableDensity } from "@/lib/use-table-density";
import { useFormatDate } from "@/hooks/use-format-date";

type ApiKeyStatus = "active" | "expired" | "revoked";

const STATUS_TONES: Record<ApiKeyStatus, "success" | "warning" | "neutral"> = {
  active: "success",
  expired: "warning",
  revoked: "neutral",
};

function getApiKeyStatus(key: ApiKey): ApiKeyStatus {
  if (key.revokedAt) return "revoked";
  if (new Date(key.expiresAt).getTime() < Date.now()) return "expired";
  return "active";
}

const DEFAULT_VALUES: CreateApiKeyInput = { name: "", expiresInDays: 90 };

// The smallest page-size option: a list that fits in one page shows no pagination bar.
const MIN_PAGE_SIZE = 25;

const INITIAL_TABLE_STATE: DataTableState = {
  page: 1,
  pageSize: 25,
  query: "",
  filters: {},
  selection: [],
};

function CreateApiKeyDrawer({
  open,
  onOpenChange,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  restoreFocusRef: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation("profile");
  const { t: tc } = useTranslation("common");
  const form = useForm<CreateApiKeyInput>({
    resolver: zodResolver(createApiKeyInputSchema),
    defaultValues: DEFAULT_VALUES,
  });
  const createMutation = useCreateApiKey();
  // The full token only exists in this state; closing the drawer drops it.
  const [token, setToken] = useState<string | null>(null);
  const tokenRef = useRef<HTMLDivElement>(null);

  // The submit button is replaced by the token: keep focus inside the drawer, on the copy button.
  useEffect(() => {
    if (token) tokenRef.current?.querySelector("button")?.focus();
  }, [token]);

  useEffect(() => {
    if (open) {
      form.reset(DEFAULT_VALUES);
      setToken(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    setToken(null);
    onOpenChange(false);
  };

  const onSubmit = async (data: CreateApiKeyInput) => {
    try {
      const created = await createMutation.mutateAsync(data);
      setToken(created.token);
    } catch (err) {
      toast.error(getErrorMessage(err, t("apiKeys.create.failed")));
    }
  };

  return (
    <FormDrawer
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      size="sm"
      title={t("apiKeys.create.title")}
      description={t("apiKeys.create.description")}
      closeLabel={tc("close")}
      restoreFocusRef={restoreFocusRef}
      busy={createMutation.isPending}
      dirty={!token && form.formState.isDirty}
      discardConfirmation={discardMessages()}
      footer={
        token ? (
          <Button type="button" onClick={close}>
            {t("apiKeys.create.done")}
          </Button>
        ) : (
          <FormActions
            busy={createMutation.isPending}
            submitLabel={t("apiKeys.create.submit")}
            submittingLabel={t("apiKeys.create.creating")}
            submitProps={{ form: "create-api-key-form" }}
          >
            <DialogClose asChild>
              <Button variant="outline">{t("apiKeys.create.cancel")}</Button>
            </DialogClose>
          </FormActions>
        )
      }
    >
      {token ? (
        <div ref={tokenRef} className="flex flex-col gap-4">
          <Alert tone="warning">{t("apiKeys.create.tokenWarning")}</Alert>
          <Field group label={t("apiKeys.create.tokenLabel")}>
            {(control) => (
              <CopyField
                id={control.id}
                role="group"
                aria-labelledby={control["aria-labelledby"]}
                value={token}
                copyLabel={t("apiKeys.create.copy")}
                copiedLabel={t("apiKeys.create.copied")}
                onCopyError={() => toast.error(t("apiKeys.create.copyFailed"))}
              />
            )}
          </Field>
        </div>
      ) : (
        <Form
          id="create-api-key-form"
          busy={createMutation.isPending}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <Field label={t("apiKeys.create.nameLabel")} error={form.formState.errors.name?.message}>
            <Input
              placeholder={t("apiKeys.create.namePlaceholder")}
              maxLength={100}
              {...form.register("name")}
            />
          </Field>
          <Field label={t("apiKeys.create.expirationLabel")}>
            {(control) => (
              <Select
                value={String(form.watch("expiresInDays"))}
                onValueChange={(value) =>
                  form.setValue(
                    "expiresInDays",
                    Number(value) as CreateApiKeyInput["expiresInDays"],
                    { shouldDirty: true },
                  )
                }
              >
                <SelectTrigger {...control}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {API_KEY_EXPIRATION_DAYS.map((days) => (
                    <SelectItem key={days} value={String(days)}>
                      {t("apiKeys.create.expirationOption", { days })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        </Form>
      )}
    </FormDrawer>
  );
}

export function ApiKeysSection() {
  const { t } = useTranslation("profile");
  const { formatDate } = useFormatDate();
  const { confirm, dialogProps } = useConfirmDialog();
  const { density } = useTableDensity();
  const [createOpen, setCreateOpen] = useState(false);
  const [state, setState] = useState(INITIAL_TABLE_STATE);
  // Focus returns here after revoking: the key's row action is gone by then.
  const tableRegion = useRef<HTMLDivElement>(null);
  const createButton = useRef<HTMLButtonElement>(null);
  const query = useQuery(apiKeysQueryOptions);
  const revokeMutation = useRevokeApiKey();

  const handleRevoke = (key: ApiKey) => {
    confirm({
      title: t("apiKeys.revoke.title"),
      description: t("apiKeys.revoke.description", { name: key.name }),
      confirmLabel: t("apiKeys.revoke.confirmLabel"),
      variant: "destructive",
      restoreFocusRef: tableRegion,
      onConfirm: async () => {
        try {
          await revokeMutation.mutateAsync(key.id);
          toast.success(t("apiKeys.revoke.success"));
        } catch (err) {
          toast.error(getErrorMessage(err, t("apiKeys.revoke.failed")));
        }
      },
    });
  };

  const columns: DataTableColumn<ApiKey>[] = [
    {
      id: "name",
      header: t("apiKeys.columns.name"),
      variant: "identity",
      rowHeader: true,
      cell: (key) => <TableIdentity name={key.name} detail={`sg_live_${key.prefix}…`} />,
    },
    {
      id: "status",
      header: t("apiKeys.columns.status"),
      width: "7.5rem",
      cell: (key) => {
        const status = getApiKeyStatus(key);
        return (
          <StatusBadge tone={STATUS_TONES[status]}>{t(`apiKeys.status.${status}`)}</StatusBadge>
        );
      },
    },
    {
      id: "created",
      header: t("apiKeys.columns.created"),
      width: "7.5rem",
      variant: "numeric",
      cell: (key) => formatDate(key.createdAt),
    },
    {
      id: "expires",
      header: t("apiKeys.columns.expires"),
      width: "7.5rem",
      variant: "numeric",
      cell: (key) => formatDate(key.expiresAt),
    },
    {
      id: "lastUsed",
      header: t("apiKeys.columns.lastUsed"),
      width: "7.5rem",
      variant: "numeric",
      cell: (key) => (key.lastUsedAt ? formatDate(key.lastUsedAt) : t("apiKeys.never")),
    },
  ];

  const page = localTablePage(query.data ?? [], state, { columns });

  return (
    <>
      <SettingsSection
        title={t("apiKeys.title")}
        description={t("apiKeys.description")}
        actions={
          <Button ref={createButton} size="sm" onClick={() => setCreateOpen(true)}>
            <Plus />
            {t("apiKeys.create.title")}
          </Button>
        }
      >
        <DataTable
          label={t("apiKeys.title")}
          bordered={false}
          density={density}
          regionRef={tableRegion}
          columns={columns}
          rows={page.rows}
          getRowId={(key) => key.id}
          state={state}
          onStateChange={setState}
          loading={query.isPending}
          refreshing={listRefreshing(query)}
          error={query.isError ? { title: t("apiKeys.loadFailed") } : null}
          onRetry={() => void query.refetch()}
          pagination={
            page.total > MIN_PAGE_SIZE
              ? { kind: "offset", total: page.total, labels: paginationLabels() }
              : undefined
          }
          rowActions={{
            label: t("apiKeys.columns.actions"),
            width: "6rem",
            render: (key) =>
              getApiKeyStatus(key) === "active" ? (
                <Button
                  variant="ghost"
                  size="compact"
                  aria-label={t("apiKeys.revoke.actionFor", { name: key.name })}
                  onClick={() => handleRevoke(key)}
                >
                  {t("apiKeys.revoke.action")}
                </Button>
              ) : null,
          }}
          messages={tableMessages({
            empty: {
              icon: <KeyRound />,
              title: t("apiKeys.empty.title"),
              description: t("apiKeys.empty.description"),
            },
          })}
        />
        <p className="text-dim">
          {t("apiKeys.cliHint")} <Mono>senseguard auth login --url {API_BASE_URL}</Mono>
        </p>
      </SettingsSection>

      <ConfirmDialog {...dialogProps} />

      <CreateApiKeyDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        restoreFocusRef={createButton}
      />
    </>
  );
}
